import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { ImportDialog } from "../components/ImportDialog";

/**
 * Generic CRUD table page.
 * props:
 *   title, subtitle, endpoint, fields:[{name,label,type,placeholder,required}]
 *   extraColumns, amountField (optional)
 */
export default function CrudPage({ title, subtitle, endpoint, fields, amountField, dataTestId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/${endpoint}`).then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [endpoint]);

  const openNew = () => {
    setEditing(null);
    const init = {};
    fields.forEach((f) => { init[f.name] = f.default ?? (f.type === "number" ? 0 : ""); });
    setForm(init);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    const init = {};
    fields.forEach((f) => { init[f.name] = item[f.name] ?? (f.type === "number" ? 0 : ""); });
    setForm(init);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.name] = parseFloat(payload[f.name] || 0);
      });
      if (editing) {
        await api.put(`/${endpoint}/${editing.id}`, payload);
        toast.success("Atualizado!");
      } else {
        await api.post(`/${endpoint}`, payload);
        toast.success("Criado!");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    try {
      await api.delete(`/${endpoint}/${id}`);
      toast.success("Excluído");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const total = amountField ? items.reduce((s, i) => s + (parseFloat(i[amountField]) || 0), 0) : null;

  return (
    <div data-testid={dataTestId}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`${dataTestId}-import`}>
              <UploadSimple size={16} weight="bold" className="mr-1" /> Importar lista
            </Button>
            <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${dataTestId}-add`}>
              <Plus size={16} weight="bold" className="mr-1" /> Adicionar
            </Button>
          </div>
        }
      />

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} title={title} endpoint={endpoint} fields={fields} dataTestId={dataTestId} onDone={load} />

      {total !== null && items.length > 0 && (
        <div className="mb-4 card-crisp px-5 py-4 flex items-baseline justify-between">
          <span className="text-sm text-slate-600">Total mensal</span>
          <span className="font-mono-nums text-2xl font-bold text-indigo-700">{formatCurrency(total)}</span>
        </div>
      )}

      {loading ? (
        <div className="text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nada por aqui ainda"
          description={`Adicione ${title.toLowerCase()} para começar a calcular com precisão.`}
          action={<div className="flex gap-2 justify-center"><Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`${dataTestId}-empty-import`}>Importar lista</Button><Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${dataTestId}-empty-add`}>Adicionar agora</Button></div>}
        />
      ) : (
        <div className="card-crisp overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                {fields.filter(f => !f.hideInTable).map((f) => (
                  <th key={f.name} className="px-5 py-3 font-medium">{f.label}</th>
                ))}
                <th className="px-5 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  {fields.filter(f => !f.hideInTable).map((f) => (
                    <td key={f.name} className={`px-5 py-3 ${f.type === "number" ? "font-mono-nums" : ""}`}>
                      {f.type === "number" && (f.format === "currency")
                        ? formatCurrency(item[f.name])
                        : f.type === "number" && f.suffix
                          ? `${item[f.name]}${f.suffix}`
                          : item[f.name]}
                    </td>
                  ))}
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => openEdit(item)} className="text-slate-500 hover:text-indigo-600 mr-2" data-testid={`${dataTestId}-edit-${item.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => del(item.id)} className="text-slate-500 hover:text-rose-600" data-testid={`${dataTestId}-delete-${item.id}`}>
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Novo"} - {title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name}>
                <Label>{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
                <Input
                  type={f.type || "text"}
                  step={f.type === "number" ? "0.01" : undefined}
                  value={form[f.name] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required}
                  placeholder={f.placeholder || ""}
                  data-testid={`${dataTestId}-field-${f.name}`}
                  className="mt-1"
                />
              </div>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${dataTestId}-save`}>Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
